import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type { BrewerFilterProps, BrewerSortingFields, ListBrewerModel, Order } from '../models';

interface IBrewersContext {
  brewersUpdate: boolean;
  toggleBrewersUpdate: () => void;
  brewers: ListBrewerModel[];
  setBrewers: (val: ListBrewerModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: BrewerSortingFields;
  setOrderBy: (val: BrewerSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: BrewerSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  filters: BrewerFilterProps;
  setFilters: (val: BrewerFilterProps) => void;
  resetFilters: () => void;
}

export const BrewersContext = createContext<IBrewersContext>({} as IBrewersContext);

export const BrewersProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [brewersUpdate, setBrewersUpdate] = useState(false);
  const toggleBarUpdate = (): void => setBrewersUpdate(!brewersUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<BrewerSortingFields>(toggleBarUpdate, 'name', 'asc');

  const [brewers, setBrewers] = useState<ListBrewerModel[]>([]);
  const [loading, setLoading] = useState(false);

  const emptyFilers: BrewerFilterProps = {
    name: undefined,
    status: undefined,
    amsAgreement: undefined,
    maxFixes: undefined,
    product: undefined,
    user: undefined
  };
  const [filters, setFilters] = useState<BrewerFilterProps>(emptyFilers);

  const resetFilters = (): void => setFilters(emptyFilers);

  return (
    <BrewersContext.Provider
      value={{
        brewersUpdate,
        toggleBrewersUpdate: toggleBarUpdate,
        brewers,
        setBrewers,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        filters,
        setFilters,
        resetFilters
      }}
    >
      {children}
    </BrewersContext.Provider>
  );
};
